import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import AdminLayout from '../components/layout/AdminLayout';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import SecretRoute from '../components/auth/SecretRoute';
import SecretAdminRedirect from '../components/auth/SecretAdminRedirect';

// Public pages
const HomePage = lazy(() => import('../pages/Home'));
const ProjectsPage = lazy(() => import('../pages/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('../pages/ProjectDetail'));
const BlogPage = lazy(() => import('../pages/Blog'));
const BlogDetailPage = lazy(() => import('../pages/BlogDetail'));
const ContactPage = lazy(() => import('../pages/Contact'));
const MediaPage = lazy(() => import('../pages/Media'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));

// Auth pages
const LoginPage = lazy(() => import('../pages/Login'));

// Admin pages
const AdminDashboard = lazy(() => import('../pages/admin/Dashboard'));
const BlogEditor = lazy(() => import('../pages/admin/BlogEditor'));
const ProjectsManager = lazy(() => import('../pages/admin/ProjectsManager'));
const MediaManager = lazy(() => import('../pages/admin/MediaManager'));
const ContactMessages = lazy(() => import('../pages/admin/ContactMessages'));
const ProfileEditor = lazy(() => import('../pages/Admin'));
const MailingListManager = lazy(() => import('../pages/admin/MailingList'));
const UsersManager = lazy(() => import('../pages/admin/Users'));
const RolesManager = lazy(() => import('../pages/admin/Roles'));

const withSuspense = (element: React.ReactNode) => (
  <Suspense fallback={<div>Loading…</div>}>{element}</Suspense>
);

export const routes: RouteObject[] = [
  {
    path: '/',
    element: withSuspense(<AppLayout />),
    children: [
      { index: true, element: withSuspense(<HomePage />) },
      { path: 'projects', element: withSuspense(<ProjectsPage />) },
      { path: 'projects/:slug', element: withSuspense(<ProjectDetailPage />) },
      { path: 'projects/:id', element: withSuspense(<ProjectDetailPage />) },
      { path: 'blog', element: withSuspense(<BlogPage />) },
      { path: 'blog/:slug', element: withSuspense(<BlogDetailPage />) },
      { path: 'blog/:id', element: withSuspense(<BlogDetailPage />) },
      { path: 'contact', element: withSuspense(<ContactPage />) },
      { path: 'media', element: withSuspense(<MediaPage />) },
      { path: 'profile-edit', element: withSuspense(<ProfileEditor />) },
      { path: '404', element: withSuspense(<NotFoundPage />) },
      { path: ':secretPath', element: <SecretAdminRedirect /> },
      { path: '*', element: <Navigate to="/404" replace /> }
    ]
  },
  {
    path: '/login',
    element: (
      <SecretRoute>
        {withSuspense(<LoginPage />)}
      </SecretRoute>
    )
  },
  {
    path: '/admin',
    element: (
      <SecretRoute>
        <ProtectedRoute requireAdmin={true}>
          {withSuspense(<AdminLayout />)}
        </ProtectedRoute>
      </SecretRoute>
    ),
    children: [
      { index: true, element: withSuspense(<AdminDashboard />) },
      { path: 'blog', element: withSuspense(<BlogEditor />) },
      { path: 'projects', element: withSuspense(<ProjectsManager />) },
      { path: 'media', element: withSuspense(<MediaManager />) },
      { path: 'messages', element: withSuspense(<ContactMessages />) },
      { path: 'profile', element: withSuspense(<ProfileEditor />) },
      { path: 'users', element: withSuspense(<UsersManager />) },
      { path: 'roles', element: withSuspense(<RolesManager />) },
      { path: 'mailing-list', element: withSuspense(<MailingListManager />) }
    ]
  }
];

export const router = createBrowserRouter(routes);

export default router;
